// "Save" for a seed made in the browser from nothing: it becomes a new seed,
// v1, yours (lib/save-root-seed.ts). After that it is an ordinary seed — the
// room reopens on it, where Save makes v2 and Start jam works.

import { useState } from "react";
import { Save } from "lucide-react";
import { Button } from "@web-shared/components/ui/button";
import { Input } from "@web-shared/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@web-shared/components/ui/dialog";
import type { CanonicalRoom } from "@shared/sync/state.js";
import { track as trackAnalytics } from "@web-shared/lib/analytics";
import { saveRoomAsNewSeed, type SaveStage } from "@/lib/save-root-seed";
import { useAuthGate } from "../auth";

const STAGE_LABEL: Record<SaveStage, string> = {
  rendering: "Rendering the mix…",
  preparing: "Preparing…",
  uploading: "Uploading…",
  finishing: "Checking it arrived…",
};

export function SaveNewSeed({
  title,
  onTitle,
  room,
  tempo,
  bytesOf,
  renderMaster,
  onSaved,
}: {
  title: string;
  onTitle: (title: string) => void;
  room: CanonicalRoom;
  tempo: number;
  bytesOf: (key: string) => Promise<Uint8Array | null>;
  renderMaster: () => Promise<AudioBuffer>;
  /** The seed exists: the page moves on to it. */
  onSaved: (seedId: string) => void;
}) {
  const gate = useAuthGate();
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState("");
  const [note, setNote] = useState("");
  const [stage, setStage] = useState<SaveStage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const saving = stage !== null;
  const empty = !room.clips.some((c) => (c.clipKind === "midi" ? (c.notes?.length ?? 0) > 0 : c.audioKey !== null));

  const save = async () => {
    setError(null);
    setStage("rendering");
    try {
      const { seedId } = await saveRoomAsNewSeed({
        room,
        tempo,
        title: title.trim() || "Untitled seed",
        key: key.trim(),
        note: note.trim() || null,
        bytesOf,
        renderMaster,
        onStage: setStage,
      });
      trackAnalytics("seed_created", { seedId, detail: "browser", value: room.tracks.length });
      onSaved(seedId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "The save didn't go through.");
    } finally {
      setStage(null);
    }
  };

  return (
    <>
      <Button
        size="sm"
        onClick={() => gate.requireSignIn("save_seed", "Sign in to save your seed", () => setOpen(true))}
        className="gap-1.5"
        title="Save as a new seed"
      >
        <Save className="size-3.5" />
        <span className="hidden sm:inline">Save seed</span>
      </Button>
      <Dialog open={open} onOpenChange={(o) => !saving && setOpen(o)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save your seed</DialogTitle>
            <DialogDescription>It goes up as a new seed, v1 — yours. Anyone can remix it or jam on it after.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Input
              value={title}
              onChange={(e) => onTitle(e.target.value)}
              placeholder="Name"
              aria-label="Name"
              maxLength={120}
              autoFocus
              disabled={saving}
            />
            <Input value={key} onChange={(e) => setKey(e.target.value)} placeholder="Key (optional) — e.g. A minor" aria-label="Key" maxLength={24} disabled={saving} />
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" aria-label="Note" maxLength={280} disabled={saving} />
          </div>
          {empty && <p className="mt-2 text-xs text-muted-foreground">There's nothing to hear yet — add a note or a sound first.</p>}
          {error && <p className="mt-2 text-xs text-removed">{error}</p>}
          <div className="mt-3 flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button size="sm" onClick={save} disabled={saving || empty || title.trim().length === 0}>
              {stage ? STAGE_LABEL[stage] : "Save"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
